import React, { lazy, Suspense, useEffect } from 'react';
import { Redirect, Switch, useLocation } from 'react-router-dom';
import { Route } from '@modernhealth/web-analytics';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { PreSessionAssessmentsIntroPage } from 'src/modules/assessments/pages/PreSessionAssessmentsIntroPage';
import { PreSessionAssessmentsPage } from 'src/modules/assessments/pages/PreSessionAssessmentsPage';
import { PreSessionAssessmentsScorePage } from 'src/modules/assessments/pages/PreSessionAssessmentsScorePage';

import { CompletionPage } from 'src/modules/auth/screens/CompletionPage';
import { IneligiblePage } from 'src/modules/auth/screens/IneligiblePage';
import { LandingPage } from 'src/modules/auth/screens/LandingPage';
import { PasswordFormPage } from 'src/modules/auth/screens/PasswordFormPage';
import { PreferredEmailPage } from 'src/modules/auth/screens/PreferredEmailPage';
import { PreferredEmailSentPage } from 'src/modules/auth/screens/PreferredEmailSentPage';
import { RegistrationLinkNotValidPage } from 'src/modules/auth/screens/RegistrationLinkNotValidPage';
import { RegistrationPage } from 'src/modules/auth/screens/RegistrationPage';
import { StartAssessmentPage } from 'src/modules/auth/screens/StartAssessmentPage';
import { HomePage } from 'src/modules/dashboard/pages/HomePage';
import { IntakeFormPage } from 'src/modules/intakeForm/pages/IntakeFormPage';
import { GetProgramRecommendationsPage } from 'src/modules/onboarding/pages/GetProgramRecommendationsPage';
import { JoinACirclePage } from 'src/modules/onboarding/pages/JoinACirclePage';
import { OnboardingWellnessSurveyPage } from 'src/modules/onboarding/pages/OnboardingWellnessSurveyPage';
import { OnMyOwnPage } from 'src/modules/onboarding/pages/OnMyOwnPage';
import { PickYourTypeOfCarePage } from 'src/modules/onboarding/pages/PickYourTypeOfCarePage';
import { PrivacyPage } from 'src/modules/onboarding/pages/PrivacyPage';
import WorkWithMentalHealthCoachPage from 'src/modules/providers/pages/WorkWithMentalHealthCoachPage';
import { DailyPause } from 'src/modules/scheduledContent/pages/DailyPause';
import { DailyPauseCelebrationContainer } from 'src/modules/scheduledContent/pages/DailyPauseCelebrationContainer';
import { DailyPauseAdditionalOptions } from 'src/modules/scheduledContent/pages/DailyPauseAdditionalOptions';
import { DailyPauseMilestones } from 'src/modules/scheduledContent/pages/DailyPauseMilestones';
import { CompletedDailyPauses } from 'src/modules/scheduledContent/pages/CompletedDailyPauses';
import { RateVideoSessionPage } from 'src/modules/videoSessions/pages/RateVideoSessionPage';
import { RegistrationBasicsForm } from 'src/modules/auth/containers/RegistrationBasicsForm';

import { WellbeingReportPage } from 'src/modules/assessments/pages/WellbeingReportPage';
import { RetakeAssessmentsPage } from 'src/modules/assessments/pages/RetakeAssessmentsPage';
import PasswordResetRequestForm from 'src/modules/auth/containers/PasswordResetRequestForm';
import PasswordResetForm from 'src/modules/auth/containers/PasswordResetForm';
import LoginForm from 'src/modules/auth/containers/LoginForm';
import { LoginForm as LoginFormSSOOnly } from 'src/modules/auth/containers/ssoOnly/LoginForm';
import { RegisterForm as RegisterFormSSOOnly } from 'src/modules/auth/containers/ssoOnly/RegisterForm';
import SingleSignOnFailed from 'src/modules/auth/containers/SingleSignOnFailed';
import SingleSignOnForm from 'src/modules/auth/containers/SingleSignOnForm';
import SingleSignOnVerify from 'src/modules/auth/containers/SingleSignOnVerify';
import EmailAddressConfirmed from 'src/modules/containers/EmailAddressConfirmed';
import ProvideAdditionalInfoPage from 'src/modules/dashboard/pages/ProvideAdditionalInfoPage';
import { Phq9TakeoverPage } from 'src/modules/dashboard/pages/Phq9TakeoverPage';
import { Program } from 'src/modules/newPrograms/pages/Program';
import { OnboardingCategoriesStepPage } from 'src/modules/onboarding/pages/OnboardingCategoriesStepPage';
import { OnboardingSurveyStepPage } from 'src/modules/onboarding/pages/OnboardingSurveyStepPage';
import OnboardingWho5IntroStepPage from 'src/modules/onboarding/pages/OnboardingWho5IntroStepPage';
import WelcomeUserPage from 'src/modules/onboarding/pages/WelcomeUserPage';
import WorkWithCoachPage from 'src/modules/providers/pages/WorkWithCoachPage';
import WorkWithTherapistPage from 'src/modules/providers/pages/WorkWithTherapistPage';
import WorkWithPammProviderRequestedPage from 'src/modules/providers/pages/WorkWithPammProviderRequestedPage';
import RequestPsychiatryProviderPage from 'src/modules/providers/pages/RequestPsychiatryProviderPage';
import { EmailConfirmationTokenExpiredPage } from 'src/modules/user/pages/EmailConfirmationTokenExpiredPage';
import EmailUnsubscribePage from 'src/modules/user/pages/EmailUnsubscribePage';
import { BenefitPerCategoryPage } from 'src/modules/benefits/pages/BenefitPerCategoryPage';
import ScheduleSessionCalendarPage from 'src/modules/videoSessions/pages/ScheduleSessionCalendarPage';
import { ScheduleSessionProviderSelectionPage } from 'src/modules/videoSessions/pages/ScheduleSessionProviderSelectionPage';
import ScheduleSessionReadonlyCalendarPage from 'src/modules/videoSessions/pages/ScheduleSessionReadonlyCalendarPage';
import VideoSessionPage from 'src/modules/videoSessions/pages/VideoSessionPage';
import SessionBookedRedirectPage from 'src/modules/videoSessions/pages/SessionBookedRedirectPage';

import { CheckForInsurancePage } from 'src/modules/insuranceIntegration/pages/CheckForInsurancePage';
import { EnterInsuranceInfoPage } from 'src/modules/insuranceIntegration/pages/EnterInsuranceInfoPage';
import { ExtendTherapySessionsPage } from 'src/modules/insuranceIntegration/pages/ExtendTherapySessionsPage';
import { InsuranceApprovedPage } from 'src/modules/insuranceIntegration/pages/InsuranceApprovedPage';
import { InsuranceIneligiblePage } from 'src/modules/insuranceIntegration/pages/InsuranceIneligiblePage';
import { InsuranceInactivePage } from 'src/modules/insuranceIntegration/pages/InsuranceInactivePage';
import { InsuranceErrorPage } from 'src/modules/insuranceIntegration/pages/InsuranceErrorPage';
import { ExtendTherapyLastSessionPage } from 'src/modules/insuranceIntegration/pages/ExtendTherapyLastSessionPage';
import { PaymentsRoutes } from 'src/modules/payments/PaymentsRoutes';
import { Resources } from 'src/modules/resources/pages/Resources';
import { MediaTypePage } from 'src/modules/resources/pages/MediaTypePage';
import { MessagesPage } from 'src/modules/messaging/pages/MessagesPage';

import { CenteredSpinner } from '../shared/components/CenteredSpinner';
import { RouteGuard } from './RouteGuard';
import { AllYourBenefitsPage } from 'src/modules/benefits/pages/AllYourBenefitsPage';
import { ChooseYourSegmentPage } from 'src/modules/benefits/pages/ChooseYourSegmentPage';
import { InviteDependentPage } from 'src/modules/dependents/pages/InviteDependentPage';
import { ManageCareTeam } from 'src/modules/care/pages/ManageCareTeam';
import { TopicErrorComponent } from 'src/modules/topics/components/TopicErrorComponent';
import { PathwaysRouter } from 'src/modules/pathways/PathwaysRouter';
import { CirclesRouter } from 'src/modules/circles/CirclesRouter';
import { CareOptions } from 'src/modules/care/pages/CareOptions';
import { DailyPauseStats } from 'src/modules/scheduledContent/pages/DailyPauseStats';
import { CareNavigationNeedStep } from 'src/modules/careNavigation/pages/CareNavigationNeedStep';
import { CareNavigationTopicsStep } from 'src/modules/careNavigation/pages/CareNavigationTopicsStep';
import { CareNavigationGoalsStep } from 'src/modules/careNavigation/pages/CareNavigationGoalsStep';
import { CareNavigationContactMethodStep } from 'src/modules/careNavigation/pages/CareNavigationContactMethodStep';
import { CareNavigationContactInformationStep } from 'src/modules/careNavigation/pages/CareNavigationContactInformationStep';
import { CareNavigationDisclaimerStep } from 'src/modules/careNavigation/pages/CareNavigationDisclaimerStep';
import { CareNavigationAllSetStep } from 'src/modules/careNavigation/pages/CareNavigationAllSetStep';
import { PAMMCareOptionsPage } from 'src/modules/pamm/pages/PAMMCareOptionsPage';
import { DirectTherapyAccessPage } from 'src/modules/care/pages/DirectTherapyAccessPage';
import { HpiEligibilityFlow } from 'src/shared/models/shared';
import { NonExistentLink } from 'src/modules/auth/screens/NonExistentLink';
import { ConsentFormContainer } from 'src/modules/auth/containers/ConsentFormContainer';
import { LegalContractsPage } from 'src/modules/auth/screens/LegalContracts';
import { RegistrationSurveyPage } from 'src/modules/auth/screens/RegistrationSurveyPage';
import { OnboardingRedirect } from 'src/modules/onboarding/pages/OnboardingRedirect';
import EmrIntakeFormPage from 'src/modules/emrIntakeForm/pages/EmrIntakeFormPage';

const ProfilePage = lazy(() => import('src/modules/user/pages/ProfilePage'));
const SingleMeditationPage = lazy(
    () => import('src/modules/meditations/pages/SingleMeditationPage'),
);
const MeditationsPage = lazy(() => import('src/modules/meditations/pages/MeditationsPage'));
const ProgramsPage = lazy(() => import('src/modules/newPrograms/pages/ProgramsPage'));
const CoursesPage = lazy(() => import('src/modules/courses/pages/CoursesPage'));
const CoursePage = lazy(() => import('src/modules/courses/pages/CoursePage'));
const SingleTopicPage = lazy(() => import('src/modules/topics/pages/TopicPage'));
const TopicsPage = lazy(() => import('src/modules/topics/pages/TopicsPage'));
const RecommendedPage = lazy(() => import('src/modules/recommended/pages/RecommendedPage'));
const AdminRouter = lazy(() => import('src/modules/admin/AdminRouter'));
const CompanyCircles = lazy(() => import('src/modules/circles/pages/CirclesCompanyPage'));
const CompanyCirclesRsvp = lazy(() => import('src/modules/circles/pages/CirclesCompanyRsvpPage'));
const CompanyCirclesRsvpConfirmationPage = lazy(
    () => import('src/modules/circles/pages/CirclesCompanyRsvpConfirmationPage'),
);

export const Routes: React.FC = () => {
    const {
        releaseDiginShowDailyPauseStreaksWeb,
        startDailyPauseStreaksExperimentWeb,
        dailyPauseStreaksExperimentVariant,
        climbSsoOnly,
    } = useFlags();
    const showStreaksInfo =
        startDailyPauseStreaksExperimentWeb &&
        dailyPauseStreaksExperimentVariant === 'treatment' &&
        releaseDiginShowDailyPauseStreaksWeb;
    const location = useLocation();

    useEffect(() => {
        //we need small amount of time to let scroll set up
        setTimeout(() => window.scrollTo(0, 0), 5);
    }, [location]);

    return (
        <Suspense fallback={<CenteredSpinner />}>
            <Switch>
                <RouteGuard
                    path="/set-new-password/:uidb64?/:token?"
                    analytics={{ pageName: 'set-new-password' }}
                    component={PasswordResetForm}
                />
                <RouteGuard
                    path="/password-reset/confirm/:uidb64?/:token?"
                    analytics={{ pageName: 'confirm-reset-password' }}
                    component={PasswordResetForm}
                />
                <RouteGuard
                    path="/email-confirmed"
                    analytics={{ pageName: 'email-confirmed' }}
                    exact
                    component={EmailAddressConfirmed}
                />
                <RouteGuard
                    path="/email-confirmation-token-expired"
                    analytics={{ pageName: 'email-confirmation-token-expired' }}
                    exact
                    component={EmailConfirmationTokenExpiredPage}
                />
                <RouteGuard
                    path="/invalid-registration-token"
                    analytics={{ pageName: 'invalid-registration-link' }}
                    exact
                    component={RegistrationLinkNotValidPage}
                />
                <RouteGuard
                    path="/ineligible"
                    analytics={{ pageName: 'registration-ineligible' }}
                    exact
                    component={IneligiblePage}
                />
                <RouteGuard
                    path="/non-existent-link"
                    analytics={{ pageName: 'non-existent-link' }}
                    exact
                    component={NonExistentLink}
                />
                <RouteGuard
                    path="/dependent-invitation"
                    analytics={{ pageName: 'dependent-invitation' }}
                    exact
                    component={RegistrationPage}
                />
                <Route
                    path="/sso-verification/:token"
                    analytics={{ pageName: 'sso-verification' }}
                    exact
                    component={SingleSignOnVerify}
                />
                <Route
                    path="/sso-failed"
                    analytics={{ pageName: 'sso-failed' }}
                    exact
                    component={SingleSignOnFailed}
                />

                <RouteGuard
                    path="/rate-session"
                    analytics={{ pageName: 'video-session-rating' }}
                    exact
                    component={RateVideoSessionPage}
                    secured
                />

                <RouteGuard
                    path="/welcome"
                    analytics={{ pageName: 'onboarding-welcome' }}
                    exact
                    component={WelcomeUserPage}
                    secured
                />
                <RouteGuard
                    path="/onboarding"
                    analytics={{ pageName: 'onboarding-redirect' }}
                    exact
                    component={OnboardingRedirect}
                    secured
                />
                <RouteGuard
                    path="/onboarding-privacy"
                    analytics={{ pageName: 'onboarding-privacy' }}
                    exact
                    component={PrivacyPage}
                    secured
                />
                <RouteGuard
                    path="/onboarding-categories/:providerSelectionMode"
                    analytics={{ pageName: 'onboarding-topic-selection' }}
                    exact
                    component={OnboardingCategoriesStepPage}
                    secured
                    noLayout
                />
                <RouteGuard
                    path="/onboarding-wellness"
                    analytics={{ pageName: 'onboarding-subtopic-selection' }}
                    exact
                    component={OnboardingWellnessSurveyPage}
                    secured
                    noLayout
                />
                <RouteGuard
                    path="/a-few-questions"
                    analytics={{ pageName: 'onboarding-assessment-intro' }}
                    exact
                    component={OnboardingWho5IntroStepPage}
                    secured
                    noLayout
                />
                <RouteGuard
                    path="/onboarding-survey"
                    analytics={{ pageName: 'onboarding-assessment-survey' }}
                    exact
                    component={OnboardingSurveyStepPage}
                    secured
                    noLayout
                />
                <RouteGuard
                    path="/on-my-own"
                    analytics={{ pageName: 'care-recommendation' }}
                    exact
                    component={OnMyOwnPage}
                    secured
                />
                <RouteGuard
                    path="/join-a-circle"
                    analytics={{ pageName: 'join-a-circle' }}
                    exact
                    component={JoinACirclePage}
                    secured
                />
                <RouteGuard
                    path="/pick-your-type-of-care"
                    analytics={{ pageName: 'onboarding-pick-your-type-of-care' }}
                    exact
                    component={PickYourTypeOfCarePage}
                    secured
                    noLayout
                />
                <RouteGuard
                    path="/get-program-recommendations"
                    analytics={{ pageName: 'care-recommendation' }}
                    exact
                    component={GetProgramRecommendationsPage}
                    secured
                />

                <RouteGuard
                    path="/pre-session-assessments-intro"
                    analytics={{ pageName: 'pre-session-assessments-intro' }}
                    component={PreSessionAssessmentsIntroPage}
                    secured
                    noLayout
                />
                <RouteGuard
                    path="/pre-session-assessments"
                    analytics={{ pageName: 'pre-session-assessments' }}
                    component={PreSessionAssessmentsPage}
                    secured
                    noLayout
                />
                <RouteGuard
                    path="/pre-session-assessments-score"
                    analytics={{ pageName: 'pre-session-assessments-score' }}
                    component={PreSessionAssessmentsScorePage}
                    secured
                    noLayout
                />
                <Redirect from="/authenticated" to="/dashboard" exact />
                <Route
                    path="/video-session/:uuid?"
                    analytics={{ pageName: 'video-session' }}
                    component={VideoSessionPage}
                />

                <Route
                    path="/email-unsubscribe/:email"
                    analytics={{ pageName: 'email-unsubscribe' }}
                    exact
                    component={EmailUnsubscribePage}
                />

                <Route
                    path="/provider-dashboard"
                    analytics={{ pageName: 'provider-dashboard' }}
                    component={() => {
                        window.location.href = process.env.REACT_APP_PROVIDER_DASH_URL || '/';
                        return null;
                    }}
                />

                <RouteGuard
                    path="/registration"
                    analytics={{ pageName: 'registration' }}
                    exact
                    component={RegistrationPage}
                />
                <RouteGuard
                    path="/legal-contracts"
                    analytics={{ pageName: 'legal-contracts' }}
                    exact
                    component={LegalContractsPage}
                />
                <RouteGuard
                    path="/preferred-email"
                    analytics={{ pageName: 'registration-perferred-email' }}
                    component={PreferredEmailPage}
                    secured
                    noLayout
                />
                <RouteGuard
                    path="/preferred-email-sent"
                    analytics={{ pageName: 'registration-perferred-email-sent' }}
                    component={PreferredEmailSentPage}
                    secured
                    noLayout
                />
                <RouteGuard
                    path="/consent-form-page"
                    analytics={{ pageName: 'registration-consent' }}
                    component={ConsentFormContainer}
                />
                <RouteGuard
                    path="/password-form-page"
                    analytics={{ pageName: 'registration-set-password' }}
                    component={PasswordFormPage}
                />
                <RouteGuard
                    path="/completion-page"
                    analytics={{ pageName: 'registration-complete' }}
                    component={CompletionPage}
                    noLayout
                    secured
                />
                <RouteGuard
                    path="/registration-survey"
                    analytics={{ pageName: 'registration-survey' }}
                    component={RegistrationSurveyPage}
                    secured
                    noLayout
                />
                <RouteGuard
                    path="/start-assessment-page"
                    analytics={{ pageName: 'start-assessment' }}
                    component={StartAssessmentPage}
                    secured
                    noLayout
                />

                <RouteGuard
                    path="/registration-basics"
                    analytics={{ pageName: 'registration-basics' }}
                    component={RegistrationBasicsForm}
                    unauthenticatedOnly
                />
                <RouteGuard
                    path="/takeover-page"
                    analytics={{ pageName: 'phq9-takeover' }}
                    component={Phq9TakeoverPage}
                    secured
                    noLayout
                />

                <RouteGuard
                    exact
                    path="/"
                    analytics={{ pageName: 'public-landing' }}
                    component={LandingPage}
                    unauthenticatedOnly
                />
                {climbSsoOnly && (
                    <RouteGuard
                        path="/login"
                        analytics={{ pageName: 'log-in' }}
                        component={LoginFormSSOOnly}
                        unauthenticatedOnly
                    />
                )}
                {climbSsoOnly && (
                    <RouteGuard
                        path="/register"
                        analytics={{ pageName: 'register' }}
                        component={RegisterFormSSOOnly}
                        unauthenticatedOnly
                    />
                )}
                {!climbSsoOnly && (
                    <RouteGuard
                        path="/login"
                        analytics={{ pageName: 'log-in' }}
                        component={LoginForm}
                        unauthenticatedOnly
                    />
                )}
                <RouteGuard
                    path="/password-reset"
                    analytics={{ pageName: 'reset-password' }}
                    exact
                    component={PasswordResetRequestForm}
                />
                <RouteGuard
                    path="/single-sign-on"
                    analytics={{ pageName: 'single-sign-on' }}
                    exact
                    component={SingleSignOnForm}
                    unauthenticatedOnly
                />
                <RouteGuard
                    path="/wellbeing-check-in"
                    analytics={{ pageName: 'wellbeing-check-in' }}
                    component={RetakeAssessmentsPage}
                    secured
                />
                <RouteGuard
                    path="/wellbeing-report"
                    analytics={{ pageName: 'wellbeing-report' }}
                    component={WellbeingReportPage}
                    secured
                />
                <RouteGuard
                    path="/provide-additional-info"
                    analytics={{ pageName: 'additional-info' }}
                    component={ProvideAdditionalInfoPage}
                    secured
                />
                <RouteGuard
                    path="/start"
                    analytics={{ pageName: 'home' }}
                    component={HomePage}
                    secured
                />
                <RouteGuard
                    path="/dashboard/:section?/:sectionState?"
                    analytics={{ pageName: 'home' }}
                    component={HomePage}
                    secured
                />
                <RouteGuard
                    path="/profile"
                    analytics={{ pageName: 'profile' }}
                    component={ProfilePage}
                    secured
                />
                <RouteGuard
                    path="/pause/complete"
                    analytics={{ pageName: 'daily-pause-complete' }}
                    component={DailyPauseCelebrationContainer}
                    exact
                    secured
                />
                {showStreaksInfo && (
                    <RouteGuard
                        path="/pause/milestones"
                        analytics={{ pageName: 'daily-pause-milestones' }}
                        component={DailyPauseMilestones}
                        exact
                        secured
                    />
                )}
                <RouteGuard
                    path="/pause/additional-options"
                    analytics={{
                        pageName: 'daily-pause-notifications-and-reminders-preferences',
                    }}
                    component={DailyPauseAdditionalOptions}
                    exact
                    secured
                />
                {showStreaksInfo && (
                    <RouteGuard
                        path="/pause/stats"
                        analytics={{ pageName: 'daily-pause-stats' }}
                        component={DailyPauseStats}
                        exact
                        secured
                    />
                )}
                {showStreaksInfo && (
                    <RouteGuard
                        path="/pause/completed-list"
                        analytics={{ pageName: 'daily-pause-completed-list' }}
                        component={CompletedDailyPauses}
                        exact
                        secured
                    />
                )}
                <RouteGuard
                    path="/pause/:uuid?"
                    analytics={{ pageName: 'daily-pause' }}
                    component={DailyPause}
                    secured
                />
                <RouteGuard
                    path="/courses"
                    analytics={{ pageName: 'courses' }}
                    component={CoursesPage}
                    secured
                />
                <RouteGuard
                    path="/course/:slug"
                    analytics={{ pageName: 'course' }}
                    component={CoursePage}
                    secured
                />
                <RouteGuard
                    path="/programs"
                    analytics={{ pageName: 'programs' }}
                    component={ProgramsPage}
                    secured
                />
                <RouteGuard
                    path="/program/:slug"
                    analytics={{ pageName: 'program' }}
                    component={Program}
                    secured
                />
                <RouteGuard
                    path="/meditations"
                    analytics={{ pageName: 'meditations' }}
                    component={MeditationsPage}
                    secured
                />
                <RouteGuard
                    path="/meditation/:slug"
                    analytics={{ pageName: 'meditation' }}
                    component={SingleMeditationPage}
                    secured
                />
                <RouteGuard
                    path="/recommended"
                    analytics={{ pageName: 'recommended' }}
                    component={RecommendedPage}
                    secured
                />
                <RouteGuard
                    path="/topic/:slug"
                    analytics={{ pageName: 'topic' }}
                    component={SingleTopicPage}
                    secured
                />
                <RouteGuard
                    path="/topics/error"
                    analytics={{ pageName: 'topic-error' }}
                    component={TopicErrorComponent}
                    secured
                />
                <RouteGuard
                    path="/topics"
                    analytics={{ pageName: 'topics' }}
                    component={TopicsPage}
                    secured
                />
                <RouteGuard
                    path="/resources/:activeTab?"
                    analytics={{ pageName: 'resources' }}
                    component={Resources}
                    secured
                />
                <RouteGuard
                    path="/pathways"
                    analytics={{ pageName: 'pathways-router' }}
                    component={PathwaysRouter}
                    noLayout
                    secured
                />
                <RouteGuard
                    path="/payments"
                    analytics={{ pageName: 'session-payments' }}
                    component={PaymentsRoutes}
                    secured
                />
                <RouteGuard
                    path="/circles"
                    analytics={{ pageName: 'circles-router' }}
                    component={CirclesRouter}
                    secured
                />
                <RouteGuard
                    path="/private-circles/:topicSlug/:companyId/join"
                    analytics={{ pageName: 'company-circles' }}
                    component={CompanyCircles}
                    noLayout
                />
                <RouteGuard
                    path="/private-circles/:topicSlug/registered/:rsvpId/confirmation"
                    analytics={{ pageName: 'company-circles-confirmation' }}
                    component={CompanyCirclesRsvpConfirmationPage}
                    noLayout
                />
                <RouteGuard
                    path="/private-circles/:topicSlug/registered/:rsvpId"
                    analytics={{ pageName: 'company-circles' }}
                    component={CompanyCirclesRsvp}
                    secured
                />
                <RouteGuard
                    path="/work-with-a-therapist"
                    analytics={{ pageName: 'work-with-a-provider' }}
                    exact
                    component={WorkWithTherapistPage}
                    secured
                />
                <RouteGuard
                    path="/work-with-a-coach"
                    analytics={{ pageName: 'work-with-a-provider' }}
                    exact
                    component={WorkWithCoachPage}
                    secured
                />
                <RouteGuard
                    path="/work-with-a-mental-health-coach"
                    analytics={{ pageName: 'work-with-a-provider' }}
                    exact
                    component={WorkWithMentalHealthCoachPage}
                    secured
                />
                <RouteGuard
                    path="/schedule-session"
                    analytics={{ pageName: 'schedule-session' }}
                    component={ScheduleSessionProviderSelectionPage}
                    exact
                    secured
                />
                <Route
                    // @deprecated
                    path="/schedule-session/select-coach"
                    analytics={{ pageName: 'schedule-session-select-coach' }}
                    // redirecting to more generic url structure and preserving location state
                    render={() => <Redirect to={{ ...location, pathname: '/schedule-session' }} />}
                />
                <RouteGuard
                    path="/schedule-session/request-provider"
                    analytics={{ pageName: 'schedule-session-request-pamm' }}
                    component={RequestPsychiatryProviderPage}
                    secured
                />
                <RouteGuard
                    path="/schedule-session/request-provider-success"
                    analytics={{
                        pageName: 'schedule-session-request-provider-success',
                    }}
                    component={WorkWithPammProviderRequestedPage}
                    secured
                />
                <RouteGuard
                    // If the user has submitted their intake form to coach, the IntakeForm page
                    // will send the user on to the appropriate flow unless `reviewIntent` is "review",
                    // in which case the user will be show the intake form review screen.
                    path="/care-form/:coachingAssignmentUuid?/:reviewIntent?"
                    analytics={{ pageName: 'care-form' }}
                    exact
                    component={IntakeFormPage}
                    secured
                />
                {/* HPI */}
                <RouteGuard
                    path="/extend-therapy-sessions"
                    analytics={{ pageName: 'extend-therapy-sessions' }}
                    exact
                    component={ExtendTherapySessionsPage}
                    secured
                />
                <RouteGuard
                    path="/extend-therapy-last-session"
                    analytics={{ pageName: 'extend-therapy-last-session' }}
                    exact
                    component={ExtendTherapyLastSessionPage}
                    secured
                />
                <RouteGuard
                    path="/inquire-employer-insurance-coverage"
                    analytics={{
                        pageName: 'inquire-insurance-coverage',
                        hpiEligibilityFlow: HpiEligibilityFlow.Therapy,
                    }}
                    exact
                    component={CheckForInsurancePage}
                    secured
                />
                <RouteGuard
                    path="/enter-insurance-plan-information"
                    analytics={{
                        pageName: 'enter-insurance-info',
                        hpiEligibilityFlow: HpiEligibilityFlow.Therapy,
                    }}
                    exact
                    component={EnterInsuranceInfoPage}
                    secured
                />
                <RouteGuard
                    path="/insurance-approved"
                    analytics={{
                        pageName: 'insurance-approved',
                        hpiEligibilityFlow: HpiEligibilityFlow.Therapy,
                    }}
                    exact
                    component={InsuranceApprovedPage}
                    secured
                />
                <RouteGuard
                    path="/insurance-ineligible"
                    analytics={{
                        pageName: 'insurance-ineligible',
                        hpiEligibilityFlow: HpiEligibilityFlow.Therapy,
                    }}
                    exact
                    component={InsuranceIneligiblePage}
                    secured
                />
                <RouteGuard
                    path="/insurance-inactive"
                    analytics={{
                        pageName: 'insurance-inactive',
                        hpiEligibilityFlow: HpiEligibilityFlow.Therapy,
                    }}
                    exact
                    component={InsuranceInactivePage}
                    secured
                />
                <RouteGuard
                    path="/insurance-error"
                    analytics={{
                        pageName: 'insurance-error',
                        hpiEligibilityFlow: HpiEligibilityFlow.Therapy,
                    }}
                    exact
                    component={InsuranceErrorPage}
                    secured
                />
                {/* Member-facing scheduling */}
                <RouteGuard
                    path="/schedule-session/calendar"
                    analytics={{ pageName: 'schedule-session-calendar' }}
                    component={ScheduleSessionCalendarPage}
                    secured
                />
                {/* From completed payment iframe into success screen */}
                <RouteGuard
                    path="/session-booked"
                    analytics={{ pageName: 'session-booked' }}
                    component={SessionBookedRedirectPage}
                    secured
                />
                {/* From provider dashboard for scheduling preview */}
                <RouteGuard
                    path="/availability-readonly/:coachUuid"
                    analytics={{
                        pageName: 'schedule-session-readonly-calendar',
                    }}
                    component={ScheduleSessionReadonlyCalendarPage}
                    redirectTo="/login"
                    secured
                />
                <RouteGuard
                    path="/admin"
                    analytics={{ pageName: 'admin-router' }}
                    component={AdminRouter}
                    redirectTo="/login"
                    secured
                />
                <RouteGuard
                    path="/standard-assessment"
                    analytics={{ pageName: 'standard-assessment' }}
                    component={RetakeAssessmentsPage}
                    secured
                    additionalProps={{
                        isFullAssessment: true,
                    }}
                />
                <RouteGuard
                    path="/extended-assessment"
                    analytics={{ pageName: 'extended-assessment' }}
                    component={RetakeAssessmentsPage}
                    secured
                    additionalProps={{
                        isFullAssessment: true,
                    }}
                />
                <RouteGuard
                    path="/benefit-per-category"
                    analytics={{ pageName: 'benefit-per-category' }}
                    component={BenefitPerCategoryPage}
                    secured
                />
                <RouteGuard
                    path="/all-your-benefits"
                    analytics={{ pageName: 'all-your-benefits' }}
                    component={AllYourBenefitsPage}
                    secured
                />
                <RouteGuard
                    path="/choose-your-segment"
                    analytics={{ pageName: '/choose-your-segment' }}
                    component={ChooseYourSegmentPage}
                    secured
                />

                <RouteGuard
                    path="/invite-dependent"
                    analytics={{ pageName: 'invite-dependent' }}
                    component={InviteDependentPage}
                    secured
                />
                <RouteGuard
                    path="/care-navigation-request"
                    analytics={{ pageName: 'care-navigation-request' }}
                    component={CareNavigationNeedStep}
                    secured
                />
                <RouteGuard
                    path="/care-navigation-topics"
                    analytics={{ pageName: 'care-navigation-topics' }}
                    component={CareNavigationTopicsStep}
                    secured
                />
                <RouteGuard
                    path="/care-navigation-goals"
                    analytics={{ pageName: 'care-navigation-goals' }}
                    component={CareNavigationGoalsStep}
                    secured
                />
                <RouteGuard
                    path="/care-navigation-contact-method"
                    analytics={{ pageName: 'care-navigation-contact-method' }}
                    component={CareNavigationContactMethodStep}
                    secured
                />
                <RouteGuard
                    path="/care-navigation-contact-information"
                    analytics={{
                        pageName: 'care-navigation-contact-information',
                    }}
                    component={CareNavigationContactInformationStep}
                    secured
                />
                <RouteGuard
                    path="/care-navigation-disclaimer"
                    analytics={{ pageName: 'care-navigation-disclaimer' }}
                    component={CareNavigationDisclaimerStep}
                    secured
                />
                <RouteGuard
                    path="/care-navigation-all-set"
                    analytics={{ pageName: 'care-navigation-all-set' }}
                    component={CareNavigationAllSetStep}
                    secured
                />
                <RouteGuard
                    path="/messages"
                    analytics={{ pageName: 'chat' }}
                    component={MessagesPage}
                    secured
                />
                <RouteGuard
                    path="/page/:name?"
                    analytics={{ pageName: 'media-type-dynamic-content-page' }}
                    component={MediaTypePage}
                    secured
                />
                <RouteGuard
                    path="/care-team"
                    component={ManageCareTeam}
                    secured
                    analytics={{ pageName: 'care-team' }}
                />
                <RouteGuard
                    path="/care-options"
                    component={CareOptions}
                    secured
                    analytics={{ pageName: 'care-options' }}
                />
                <RouteGuard
                    path="/direct-therapy-access"
                    component={DirectTherapyAccessPage}
                    secured
                    analytics={{ pageName: 'confirm-therapy' }}
                />
                <RouteGuard
                    path="/pamm-care-options/:option/:uuid"
                    component={PAMMCareOptionsPage}
                    secured
                    analytics={{ pageName: 'pamm-care-options' }}
                />
                <RouteGuard
                    path="/contact-us"
                    component={() => {
                        window.location.replace(
                            'https://support.modernhealth.com/hc/en-us/requests/new',
                        );
                        return <CenteredSpinner />;
                    }}
                    analytics={{ pageName: 'contact-us-redirect' }}
                    secured
                />
                <RouteGuard
                    path="/intake-form/:chapter"
                    component={EmrIntakeFormPage}
                    secured
                    analytics={{ pageName: 'emr-intake-form' }}
                />
                <Redirect to="/dashboard" />
            </Switch>
        </Suspense>
    );
};
